'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { SendIcon } from 'lucide-react'
import { usePlausible } from 'next-plausible'
import { type FormEvent, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import type * as z from 'zod'
import { signupForNewsletterAction } from '@/actions/email'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { emailSignupSchema } from '@/schemas/email'

type FormValues = z.infer<typeof emailSignupSchema>

export default function SignupForm() {
  const [signedUp, setSignedUp] = useState(false)
  const plausible = usePlausible()

  const form = useForm<FormValues>({
    resolver: zodResolver(emailSignupSchema),
    defaultValues: { email: '' },
    mode: 'onChange',
  })

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      form.handleSubmit(async (data) => {
        const formData = new FormData()
        formData.append('email', data.email)

        const result = await signupForNewsletterAction(formData)
        if (!result) {
          plausible('SignupFormError')
          return void form.setError('email', {
            message: 'Something went wrong, please try again.',
          })
        }

        plausible('SignupFormSubmitted')
        setSignedUp(true)
      })(e)
    },
    [form, plausible],
  )

  if (signedUp) {
    return (
      <p className="w-full rounded bg-green-100 p-2 text-green-600 md:rounded-lg md:p-4 dark:bg-green-950">
        You&#39;re on the list! 🎉 Thank you for your continued support! We&#39;ll periodically send
        you updates on the project as they develop. You&#39;ll be able to opt-out at any time.
      </p>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="flex w-full flex-col items-start gap-2 sm:flex-row">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full grow">
              <FormControl>
                <Input type="email" placeholder="hello@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full cursor-pointer sm:w-[150px]"
          disabled={form.formState.isSubmitting}
        >
          Sign Up
          <SendIcon className="ml-2 size-4" />
        </Button>
      </form>
    </Form>
  )
}
